/**
 * App — the authenticated shell.
 *
 * What changed
 * ------------
 * The previous version booted straight into this shell with no sign-in step, and
 * kept `useState('admin')` plus two buttons that let any visitor grant
 * themselves Admin. Role now comes from the server and cannot be changed from
 * here.
 *
 * Three further fixes worth calling out:
 *
 *   - Nav filtering used to be the *only* check. `renderPage()` switched on
 *     `activePage` regardless, so anything that could set that state — including
 *     a stale value left over from a role change — rendered a privileged panel.
 *     Both layers now consult the same server permission map.
 *   - `key={activePage}` on the page container forced a full remount of every
 *     panel on every navigation, discarding in-progress uploads in the report
 *     studio. Removed; React reconciles by position and the switch already swaps
 *     component types.
 *   - The hamburger carried `style={{ display: 'none' }}`, so the off-canvas
 *     sidebar had no trigger on narrow screens. It is now shown by media query.
 */

import { useEffect, useMemo, useState } from 'react'
import './index.css'
import {
  LayoutDashboard, Users, FileText, Shield, UserCheck,
  LogOut, Menu, X, Zap, Lock, KeyRound, Loader2, ShieldAlert,
  Activity, Bell, Globe, ShieldCheck, DollarSign,
} from 'lucide-react'
import Dashboard from './components/Dashboard'
import QuickReport from './components/QuickReport'
import ClientManagement from './components/ClientManagement'
import ReportReview from './components/ReportReview'
import SecurityAudit from './components/SecurityAudit'
import UserManagement from './components/UserManagement'
import LoginScreen from './components/LoginScreen'
import ChangePasswordModal from './components/ChangePasswordModal'
import TelemetryExplorer from './components/TelemetryExplorer'
import AlertHub from './components/AlertHub'
import ApplicationHealthMap from './components/ApplicationHealthMap'
import CloudIQShield from './components/CloudIQShield'
import CloudIQCostGuard from './components/CloudIQCostGuard'
import { useAuth } from './context/AuthContext'

/**
 * `permission` names a key in the server-supplied permissions object. `null`
 * means every signed-in user may see the page.
 */
const NAV_ITEMS = [
  { key: 'quick-report', label: 'Report Studio', icon: Zap, section: 'Operations', permission: 'generate_reports' },
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, section: 'Operations', permission: null },
  { key: 'app-map', label: 'App Topology Map', icon: Globe, section: 'Operations', permission: null },
  { key: 'shield', label: 'CloudIQ Shield', icon: ShieldCheck, section: 'Operations', permission: null },
  { key: 'costguard', label: 'CloudIQ CostGuard', icon: DollarSign, section: 'Operations', permission: null },
  { key: 'telemetry', label: 'Pulse Telemetry', icon: Activity, section: 'Operations', permission: null },
  { key: 'alerts', label: 'Alert Hub', icon: Bell, section: 'Operations', permission: null },
  { key: 'clients', label: 'Clients & Agents', icon: Users, section: 'Operations', permission: 'manage_clients' },
  { key: 'reports', label: 'Report Review', icon: FileText, section: 'Operations', permission: null },
  { key: 'users', label: 'Users & RBAC', icon: UserCheck, section: 'Administration', permission: 'manage_users' },
  { key: 'security', label: 'Security & Audit', icon: Shield, section: 'Administration', permission: 'view_audit_log' },
]

const DEFAULT_PAGE = 'dashboard'

/** Shown while the stored token is being revalidated against the server. */
function CheckingSession() {
  return (
    <div className="boot-shell" role="status" aria-live="polite">
      <Loader2 size={26} className="spin" aria-hidden="true" />
      <p className="boot-text">Restoring your session…</p>
    </div>
  )
}

/**
 * Rendered in place of a panel when the active page needs a permission the user
 * does not hold. Reached by deep state rather than by clicking, since the nav
 * item is hidden.
 */
function PermissionDenied({ label, onBack }) {
  return (
    <div className="card denied-card">
      <div className="card-body denied-body">
        <div className="denied-icon">
          <ShieldAlert size={22} aria-hidden="true" />
        </div>
        <h2 className="denied-title">Insufficient permissions</h2>
        <p className="denied-text">
          Your account does not have access to <strong>{label}</strong>. If you need it, ask a
          platform administrator to update your role.
        </p>
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back to Dashboard
        </button>
      </div>
    </div>
  )
}

function AppShell() {
  const { user, can, signOut, mustChangePassword } = useAuth()

  const [activePage, setActivePage] = useState(DEFAULT_PAGE)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [pendingReports, setPendingReports] = useState(0)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const visibleNav = useMemo(
    () => NAV_ITEMS.filter((item) => !item.permission || can(item.permission)),
    [can],
  )

  const grouped = useMemo(() => {
    const sections = new Map()
    visibleNav.forEach((item) => {
      if (!sections.has(item.section)) sections.set(item.section, [])
      sections.get(item.section).push(item)
    })
    return Array.from(sections.entries())
  }, [visibleNav])

  // If the landing page is not permitted, fall back to the first one that is,
  // so nobody is dropped onto a denial panel at sign-in.
  useEffect(() => {
    const current = NAV_ITEMS.find((item) => item.key === activePage)
    if (current && (!current.permission || can(current.permission))) return
    setActivePage(visibleNav[0]?.key ?? DEFAULT_PAGE)
  }, [activePage, can, visibleNav])

  const currentPage = NAV_ITEMS.find((item) => item.key === activePage)
  const pageAllowed = !currentPage?.permission || can(currentPage.permission)

  const handleNavigate = (key) => {
    setActivePage(key)
    setSidebarOpen(false)
  }

  const handleSignOut = async () => {
    if (signingOut) return
    setSigningOut(true)
    await signOut()
    // The provider flips status to 'anonymous', which unmounts this component.
  }

  const renderPage = () => {
    // Second gate. Hiding the nav item is presentation; this is the check that
    // actually keeps a privileged panel off the screen.
    if (!pageAllowed) {
      return (
        <PermissionDenied
          label={currentPage?.label ?? 'this page'}
          onBack={() => handleNavigate(visibleNav[0]?.key ?? DEFAULT_PAGE)}
        />
      )
    }

    switch (activePage) {
      case 'app-map':
        return <ApplicationHealthMap />
      case 'shield':
        return <CloudIQShield />
      case 'costguard':
        return <CloudIQCostGuard />
      case 'telemetry':
        return <TelemetryExplorer />
      case 'alerts':
        return <AlertHub />
      case 'quick-report':
        return <QuickReport onNavigate={setActivePage} />
      case 'dashboard':
        return <Dashboard onNavigate={setActivePage} onPendingChange={setPendingReports} />
      case 'clients':
        return <ClientManagement />
      case 'reports':
        return <ReportReview />
      case 'users':
        return <UserManagement />
      case 'security':
        return <SecurityAudit />
      default:
        return <Dashboard onNavigate={setActivePage} onPendingChange={setPendingReports} />
    }
  }

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Dismisses the off-canvas sidebar on narrow screens. */}
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-scrim"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside id="app-sidebar" className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.png" alt="" className="sidebar-logo-img" />
            {/* Not an <h1>: the page title in the topbar owns that, so heading
                order starts correctly on every page. */}
            <div className="sidebar-logo-text">
              <p className="sidebar-wordmark">CloudIQ Studio</p>
              <span>executive reports at digital speed</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav" aria-label="Primary">
          {grouped.map(([section, items]) => (
            <div key={section}>
              <div className="nav-section-title">{section}</div>
              {items.map((item) => {
                const isActive = activePage === item.key
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => handleNavigate(item.key)}
                  >
                    <item.icon size={17} aria-hidden="true" />
                    <span>{item.label}</span>
                    {item.key === 'reports' && pendingReports > 0 && (
                      <span className="nav-badge">
                        {pendingReports}
                        <span className="sr-only"> reports awaiting review</span>
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="account-block">
            <p className="account-name">{user?.name || user?.email || 'Signed in'}</p>
            {user?.email && <p className="account-email">{user.email}</p>}
            <span className="account-role">
              <Lock size={11} aria-hidden="true" />
              {user?.role_label || user?.role || 'Account'}
            </span>
          </div>

          <div className="account-actions">
            <button
              type="button"
              className="btn btn-secondary account-btn"
              onClick={() => setPasswordModalOpen(true)}
            >
              <KeyRound size={14} aria-hidden="true" />
              Change password
            </button>
            <button
              type="button"
              className="btn btn-secondary account-btn"
              onClick={handleSignOut}
              disabled={signingOut}
              aria-busy={signingOut}
            >
              {signingOut ? (
                <Loader2 size={14} className="spin" aria-hidden="true" />
              ) : (
                <LogOut size={14} aria-hidden="true" />
              )}
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div className="topbar-lead">
            <button
              type="button"
              className="btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen((open) => !open)}
              aria-expanded={sidebarOpen}
              aria-controls="app-sidebar"
              aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              {sidebarOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
            </button>
            <div>
              <h1 className="topbar-title">{currentPage?.label || 'Dashboard'}</h1>
              <p className="topbar-subtitle">
                {(user?.role_label || user?.role || 'Account')} access · {today}
              </p>
            </div>
          </div>

          <div className="topbar-actions">
            <span className="topbar-identity">
              <Lock size={12} aria-hidden="true" />
              {user?.role_label || user?.role || 'Account'}
            </span>
          </div>
        </header>

        <div id="main-content" className="page-content animate-in" tabIndex={-1}>
          {renderPage()}
        </div>
      </main>

      <ChangePasswordModal
        isOpen={passwordModalOpen || mustChangePassword}
        onClose={() => setPasswordModalOpen(false)}
        required={mustChangePassword}
      />
    </>
  )
}

export default function App() {
  const { status } = useAuth()

  if (status === 'checking') return <CheckingSession />
  if (status !== 'authenticated') return <LoginScreen />
  return <AppShell />
}
